// UnknownAlumniRegistration.jsx
import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Grid,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  CardHeader,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Fade,
  LinearProgress,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import PageLayout from 'examples/LayoutContainers/PageLayout';
import api from '../../../API/axiosInstance';
import { API_ENDPOINTS } from '../../../config/api';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

const initialForm = {
  // Personal Information
  fullName: '',
  email: '',
  gender: '',
  dateOfBirth: '',
  rollNumber: '',
  batch: '',
  collegeStudied: '',
  courseStudied: '',
  branch: '',
  category: '',
  whatsAppNumber: '',
  
  // Professional Information
  presentStatus: '',
  domain: '',
  organization: '',
  designation: '',
  location: '',
  availability: '',
};

const genderOptions = ['Male', 'Female'];
const categoryOptions = ['U.G.', 'P.G.', 'M.Phil', 'Ph.D.'];

const steps = [
  { label: 'Personal & Academic', icon: <SchoolIcon /> },
  { label: 'Professional Details', icon: <WorkIcon /> },
];

const UnknownAlumniRegistration = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info', // 'success', 'error', 'warning', 'info'
  });

  // Helper: reset form
  const resetForm = useCallback(() => {
    setFormData(initialForm);
    setErrors({});
    setActiveStep(0);
  }, []);

  // Robust fetch wrapper for endpoints that sometimes return { data: ... } or raw
  const getPayload = (resp) => resp?.data?.data ?? resp?.data ?? resp;

  const handleTextChange = (field) => (e) => {
    let value = e.target.value;
    
    // Special handling for phone number - only allow numbers and common phone characters
    if (field === 'whatsAppNumber') {
      value = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle phone number key press - prevent non-numeric characters
  const handlePhoneKeyPress = (e) => {
    const char = e.key;
    // Allow: numbers, +, -, space, backspace, delete, tab, arrow keys
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 
      'ArrowUp', 'ArrowDown', 'Home', 'End'
    ];
    
    if (allowedKeys.includes(char)) {
      return; // Allow these keys
    }
    
    // Allow numbers and specific phone characters
    const phoneCharRegex = /[\d\+\-\(\)\s]/;
    if (!phoneCharRegex.test(char)) {
      e.preventDefault(); // Prevent non-phone characters
    }
  };

  // Email validation helper
  const validateEmail = (email) => {
    if (!email || !String(email).trim()) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Phone number validation helper - only numbers allowed
  const validatePhoneNumber = (phone) => {
    if (!phone || !String(phone).trim()) return false;
    // Remove spaces, dashes, and parentheses for validation
    const cleaned = phone.replace(/[\s\-\(\)]/g, '');
    // Check if contains only digits and optional + at start
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    return phoneRegex.test(cleaned);
  };

  // Format phone number - allow only numbers and +, -, spaces, parentheses
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters except +, -, spaces, and parentheses
    return value.replace(/[^\d\+\-\(\)\s]/g, '');
  };

  // Date validation - should not be future date and should be reasonable (not too old)
  const validateDateOfBirth = (date) => {
    if (!date) return { valid: false, message: 'Date of Birth is required' };
    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if date is in the future
    if (selectedDate > today) {
      return { valid: false, message: 'Date of Birth cannot be a future date' };
    }
    
    // Check if date is too old (more than 100 years ago)
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 100);
    if (selectedDate < minDate) {
      return { valid: false, message: 'Date of Birth seems too old. Please verify.' };
    }
    
    return { valid: true };
  };

  // Step validation
  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 0) {
      // Validate Personal & Academic step - all fields mandatory except RollNumber
      if (!formData.fullName || !String(formData.fullName).trim()) {
        newErrors.fullName = 'Full Name is required';
      } else if (formData.fullName.trim().length < 2) {
        newErrors.fullName = 'Full Name must be at least 2 characters';
      } else if (!/^[a-zA-Z\s\.\-']+$/.test(formData.fullName.trim())) {
        newErrors.fullName = 'Full Name can only contain letters, spaces, dots, hyphens, and apostrophes';
      }
      
      if (!formData.email || !String(formData.email).trim()) {
        newErrors.email = 'Email is required';
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      
      if (!formData.gender || !String(formData.gender).trim()) {
        newErrors.gender = 'Gender is required';
      }
      
      const dateValidation = validateDateOfBirth(formData.dateOfBirth);
      if (!dateValidation.valid) {
        newErrors.dateOfBirth = dateValidation.message;
      }
      
      // Validate Roll Number if provided (optional field)
      if (formData.rollNumber && formData.rollNumber.trim()) {
        if (formData.rollNumber.trim().length < 2) {
          newErrors.rollNumber = 'Roll Number must be at least 2 characters if provided';
        }
      }
      
      if (!formData.batch || !String(formData.batch).trim()) {
        newErrors.batch = 'Batch is required';
      } else if (formData.batch.trim().length < 4) {
        newErrors.batch = 'Batch should be at least 4 characters (e.g., 2015-2019)';
      }
      
      if (!formData.collegeStudied || !String(formData.collegeStudied).trim()) {
        newErrors.collegeStudied = 'College Studied is required';
      } else if (formData.collegeStudied.trim().length < 3) {
        newErrors.collegeStudied = 'College name must be at least 3 characters';
      }
      
      if (!formData.courseStudied || !String(formData.courseStudied).trim()) {
        newErrors.courseStudied = 'Course Studied is required';
      } else if (formData.courseStudied.trim().length < 2) {
        newErrors.courseStudied = 'Course name must be at least 2 characters';
      }
      
      if (!formData.branch || !String(formData.branch).trim()) {
        newErrors.branch = 'Branch is required';
      } else if (formData.branch.trim().length < 2) {
        newErrors.branch = 'Branch name must be at least 2 characters';
      }
      
      if (!formData.category || !String(formData.category).trim()) {
        newErrors.category = 'Category is required';
      }
      
      if (!formData.whatsAppNumber || !String(formData.whatsAppNumber).trim()) {
        newErrors.whatsAppNumber = 'WhatsApp Number is required';
      } else if (!validatePhoneNumber(formData.whatsAppNumber)) {
        newErrors.whatsAppNumber = 'Please enter a valid phone number (10-15 digits, numbers only)';
      }
    }
    
    if (step === 1) {
      // Validate Professional step - all fields mandatory
      if (!formData.presentStatus || !String(formData.presentStatus).trim()) {
        newErrors.presentStatus = 'Present Status is required';
      } else if (formData.presentStatus.trim().length < 2) {
        newErrors.presentStatus = 'Present Status must be at least 2 characters';
      }
      
      if (!formData.domain || !String(formData.domain).trim()) {
        newErrors.domain = 'Domain is required';
      } else if (formData.domain.trim().length < 2) {
        newErrors.domain = 'Domain must be at least 2 characters';
      }
      
      if (!formData.organization || !String(formData.organization).trim()) {
        newErrors.organization = 'Organization is required';
      } else if (formData.organization.trim().length < 2) {
        newErrors.organization = 'Organization name must be at least 2 characters';
      }
      
      if (!formData.designation || !String(formData.designation).trim()) {
        newErrors.designation = 'Designation is required';
      } else if (formData.designation.trim().length < 2) {
        newErrors.designation = 'Designation must be at least 2 characters';
      }
      
      if (!formData.location || !String(formData.location).trim()) {
        newErrors.location = 'Location is required';
      } else if (formData.location.trim().length < 2) {
        newErrors.location = 'Location must be at least 2 characters';
      }
      
      if (!formData.availability || !String(formData.availability).trim()) {
        newErrors.availability = 'Availability is required';
      } else if (formData.availability.trim().length < 2) {
        newErrors.availability = 'Availability must be at least 2 characters';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to show notifications
  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);

  // Close notification handler
  const handleCloseNotification = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      showNotification('Please fix the errors before proceeding', 'error');
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Prevent form submission on Enter key unless on last step
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Always prevent default Enter behavior
      e.preventDefault();
      
      // If on first step, trigger Next button click
      if (activeStep === 0) {
        handleNext();
      }
      // If on last step, do nothing - user must click the submit button explicitly
    }
  };

  // Handle Enter key on individual TextFields
  const handleTextFieldKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // If on first step, trigger Next button click
      if (activeStep === 0) {
        handleNext();
      }
      // If on last step, do nothing - prevent form submission
    }
  };

  // Submit handler
  const handleSubmit = async (e) => {
    // Prevent default form submission if event exists
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Only allow submission on the last step
    if (activeStep !== steps.length - 1) {
      return;
    }
    
    if (!validateStep(activeStep)) {
      showNotification('Please fix the errors in the form', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        fullName: formData.fullName,
        email: formData.email?.trim() || null,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString().split('T')[0] : null,
        rollNumber: formData.rollNumber?.trim() || null,
        batch: formData.batch,
        collegeStudied: formData.collegeStudied,
        courseStudied: formData.courseStudied,
        branch: formData.branch,
        category: formData.category,
        whatsAppNumber: formData.whatsAppNumber?.trim() || null,
        presentStatus: formData.presentStatus,
        domain: formData.domain,
        organization: formData.organization,
        designation: formData.designation,
        location: formData.location,
        availability: formData.availability,
      };

      const response = await api.post(API_ENDPOINTS.UNKNOWN_ALUMNI.REGISTER, payload);
      const data = getPayload(response);

      // Check for success (handle both camelCase and PascalCase)
      const isSuccess = data?.success ?? data?.Success ?? response?.data?.success ?? response?.data?.Success;
      
      if (isSuccess) {
        setSubmitted(true);
        showNotification('Registration submitted successfully! Your request will be reviewed by the administrator.', 'success');
        resetForm();
      } else {
        // Handle case where API returns success: false with a message
        const errorMsg = data?.message || data?.Message || response?.data?.message || response?.data?.Message;
        if (errorMsg) {
          showNotification(errorMsg, 'error');
        } else {
          throw new Error('Failed to submit registration');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Extract error message from various possible response structures
      let errorMessage = null;
      
      // Check for error response data (400 BadRequest, 500 InternalServerError, etc.)
      if (error.response?.data) {
        const responseData = error.response.data;
        
        // Check different possible message field names (camelCase and PascalCase)
        errorMessage = 
          responseData.message ||           // lowercase 'message'
          responseData.Message ||           // uppercase 'Message'
          responseData.error?.message ||    // nested error object (camelCase)
          responseData.error?.Message ||    // nested error object (PascalCase)
          responseData.Error ||             // uppercase 'Error'
          (typeof responseData === 'string' ? responseData : null); // direct string response
      }
      
      // Fallback to error message if no response data
      if (!errorMessage) {
        errorMessage = error.message || 'Failed to submit registration. Please try again.';
      }

      // Show error notification with the validation message
      showNotification(errorMessage, 'error');
      
      // Set field-specific errors for duplicate validation errors
      // This helps highlight the problematic field in the UI
      const lowerErrorMessage = errorMessage.toLowerCase();
      if (lowerErrorMessage.includes('email')) {
        setErrors(prev => ({ ...prev, email: errorMessage }));
      } else if (lowerErrorMessage.includes('whatsapp')) {
        setErrors(prev => ({ ...prev, whatsAppNumber: errorMessage }));
      } else if (lowerErrorMessage.includes('roll number')) {
        setErrors(prev => ({ ...prev, rollNumber: errorMessage }));
      } else if (lowerErrorMessage.includes('mobile')) {
        setErrors(prev => ({ ...prev, whatsAppNumber: errorMessage }));
      }
    } finally {
      setLoading(false);
    }
  };

  const progress = ((activeStep + 1) / steps.length) * 100;

  if (submitted) {
    return (
      <PageLayout>
        <MDBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="80vh"
          px={3}
        >
          <Card
            sx={{
              p: 4,
              maxWidth: 600,
              textAlign: 'center',
              borderRadius: theme.borders.borderRadius.lg,
              boxShadow: theme.boxShadows.xl,
            }}
          >
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width={100}
              height={100}
              borderRadius="50%"
              bgColor="success.main"
              mx="auto"
              mb={3}
            >
              <CheckCircleIcon sx={{ fontSize: 60, color: 'white' }} />
            </MDBox>
            <MDTypography variant="h4" fontWeight="bold" color="success" gutterBottom>
              Registration Submitted!
            </MDTypography>
            <MDTypography variant="body2" color="text" sx={{ mt: 2, mb: 3 }}>
              Thank you for your registration. Your request has been submitted successfully and will be reviewed by the administrator.
              You will be notified once your registration is approved.
            </MDTypography>
            <MDButton
              variant="gradient"
              color="success"
              onClick={() => setSubmitted(false)}
              sx={{ mt: 2 }}
            >
              Submit Another Registration
            </MDButton>
          </Card>
        </MDBox>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="50vh"
        py={4}
        px={3}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
        }}
      >
        <Box sx={{ maxWidth: 800 }}>
          {/* Header */}
          <MDBox textAlign="center" mb={4}>
            <MDTypography 
              variant="h3" 
              fontWeight="bold" 
              color="dark"
              sx={{ 
                mb: 1,
                fontFamily: 'serif',
              }}
            >
              Join the Community
            </MDTypography>
            <MDTypography variant="body1" color="text" sx={{ mb: 4 }}>
              Complete your profile to connect with fellow alumni.
            </MDTypography>
          </MDBox>

        
          <Card
            sx={{
              borderRadius: theme.borders.borderRadius.lg,
              boxShadow: theme.boxShadows.xl,
              overflow: 'hidden',
              border: 'none',
            }}
          >
            {/* Progress Bar */}
            <Box sx={{ height: 4, backgroundColor: theme.palette.grey[200] }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 4,
                  backgroundColor: 'transparent',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: theme.palette.success.main,
                    transition: 'transform 0.3s ease',
                  },
                }}
              />
            </Box>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Only allow form submission if we're on the last step
                if (activeStep === steps.length - 1) {
                  handleSubmit(e);
                }
              }} 
              onKeyDown={handleFormKeyDown}
            >
              <CardHeader
                sx={{
                  pb: 2,
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                <MDBox display="flex" alignItems="center" gap={2}>
                  <Chip
                    icon={steps[activeStep].icon}
                    label={activeStep + 1}
                    color="success"
                    sx={{
                      width: 40,
                      height: 40,
                      fontSize: '1rem',
                      fontWeight: 'bold',
                    }}
                  />
                  <Box>
                    <MDTypography variant="h5" fontWeight="bold" color="dark">
                      {steps[activeStep].label}
                    </MDTypography>
                    <MDTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
                      {activeStep === 0 
                        ? "Let's start with your personal and academic information."
                        : "Share your current professional status with the network."}
                    </MDTypography>
                  </Box>
                </MDBox>
              </CardHeader>

              <CardContent sx={{ minHeight: 270, py: 4 }}>
                {/* Step 1: Personal & Academic Information */}
                {activeStep === 0 && (
                  <Fade in={activeStep === 0} timeout={300}>
                    <Box>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            value={formData.fullName}
                            onChange={handleTextChange('fullName')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.fullName}
                            helperText={errors.fullName}
                            required
                            inputProps={{ maxLength: 200 }}
                            placeholder="Enter your full name"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={handleTextChange('email')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.email}
                            helperText={errors.email}
                            required
                            inputProps={{ maxLength: 200 }}
                            placeholder="Enter your email address"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Roll Number"
                            value={formData.rollNumber}
                            onChange={handleTextChange('rollNumber')}
                            onKeyDown={handleTextFieldKeyDown}
                            inputProps={{ maxLength: 50 }}
                            placeholder="Enter your roll number (optional)"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            select                            
                            value={formData.gender}
                            onChange={handleTextChange('gender')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.gender}
                            helperText={errors.gender}
                            required
                            SelectProps={{
                              native: true,
                            }}
                          >
                            <option value="">Select Gender</option>
                            {genderOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Date of Birth"
                            type="date"
                            value={formData.dateOfBirth}
                            onChange={handleTextChange('dateOfBirth')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.dateOfBirth}
                            helperText={errors.dateOfBirth}
                            required
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Batch"
                            value={formData.batch}
                            onChange={handleTextChange('batch')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.batch}
                            helperText={errors.batch}
                            required
                            placeholder="e.g., 2015-2019"
                            inputProps={{ maxLength: 20 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="College Studied"
                            value={formData.collegeStudied}
                            onChange={handleTextChange('collegeStudied')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.collegeStudied}
                            helperText={errors.collegeStudied}
                            required
                            inputProps={{ maxLength: 200 }}
                            placeholder="Enter college name"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Course Studied"
                            value={formData.courseStudied}
                            onChange={handleTextChange('courseStudied')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.courseStudied}
                            helperText={errors.courseStudied}
                            required
                            inputProps={{ maxLength: 200 }}
                            placeholder="e.g., Computer Science"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Branch"
                            value={formData.branch}
                            onChange={handleTextChange('branch')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.branch}
                            helperText={errors.branch}
                            required
                            inputProps={{ maxLength: 200 }}
                            placeholder="Enter branch/department"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            select
                           
                            value={formData.category}
                            onChange={handleTextChange('category')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.category}
                            helperText={errors.category}
                            required
                            SelectProps={{
                              native: true,
                            }}
                          >
                            <option value="">Select Category</option>
                            {categoryOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="WhatsApp Number"
                            value={formData.whatsAppNumber}
                            onChange={handleTextChange('whatsAppNumber')}
                            onKeyDown={handleTextFieldKeyDown}
                            onKeyPress={handlePhoneKeyPress}
                            error={!!errors.whatsAppNumber}
                            helperText={errors.whatsAppNumber || 'Enter numbers only (e.g., +91 9876543210)'}
                            required
                            inputProps={{ maxLength: 20 }}
                            placeholder="Enter WhatsApp number (numbers only)"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Fade>
                )}

                {/* Step 2: Professional Information */}
                {activeStep === 1 && (
                  <Fade in={activeStep === 1} timeout={300}>
                    <Box>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Present Status"
                            value={formData.presentStatus}
                            onChange={handleTextChange('presentStatus')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.presentStatus}
                            helperText={errors.presentStatus}
                            required
                            placeholder="e.g., Working, Student, etc."
                            inputProps={{ maxLength: 200 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Domain"
                            value={formData.domain}
                            onChange={handleTextChange('domain')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.domain}
                            helperText={errors.domain}
                            required
                            inputProps={{ maxLength: 100 }}
                            placeholder="e.g., Embedded, Web Application, IoT"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Organization"
                            value={formData.organization}
                            onChange={handleTextChange('organization')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.organization}
                            helperText={errors.organization}
                            required
                            inputProps={{ maxLength: 300 }}
                            placeholder="Enter company name"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Designation"
                            value={formData.designation}
                            onChange={handleTextChange('designation')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.designation}
                            helperText={errors.designation}
                            required
                            inputProps={{ maxLength: 200 }}
                            placeholder="e.g., Senior Engineer"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Location"
                            value={formData.location}
                            onChange={handleTextChange('location')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.location}
                            helperText={errors.location}
                            required
                            inputProps={{ maxLength: 200 }}
                            placeholder="Enter location"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Availability"
                            value={formData.availability}
                            onChange={handleTextChange('availability')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.availability}
                            helperText={errors.availability}
                            required
                            placeholder="e.g., India, Abroad"
                            inputProps={{ maxLength: 200 }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Fade>
                )}
              </CardContent>

              {/* Navigation Buttons */}
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
                sx={{
                  borderTop: `1px solid ${theme.palette.grey[200]}`,
                  backgroundColor: theme.palette.grey[50],
                }}
              >
                <Box>
                  {activeStep > 0 && (
                    <MDButton
                      type="button"
                      variant="outlined"
                      color="dark"
                      onClick={handleBack}
                      disabled={loading}
                      startIcon={<ChevronLeftIcon />}
                    >
                      Back
                    </MDButton>
                  )}
                </Box>
                <Box>
                  {activeStep < steps.length - 1 ? (
                    <MDButton
                      type="button"
                      variant="gradient"
                      color="success"
                      onClick={handleNext}
                      endIcon={<ChevronRightIcon />}
                    >
                      Next Step
                    </MDButton>
                  ) : (
                    <MDButton
                      type="button"
                      variant="gradient"
                      color="success"
                      disabled={loading}
                      onClick={handleSubmit}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                    >
                      {loading ? 'Processing...' : 'Complete Registration'}
                    </MDButton>
                  )}
                </Box>
              </MDBox>
            </form>
          </Card>
        </Box>
      </MDBox>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
          elevation={6}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default UnknownAlumniRegistration;
